import { CognitoUserPool } from 'amazon-cognito-identity-js';
import { gql, Resolver } from '@apollo/client';
import _get from 'lodash/get';

import { getUser, getUserAttributes, getSession } from '@trqk/utils';
import { GET_SESSION } from './getSession';
import { GET_STATEMENTS_UI_STATE } from '../uiState/getStatementsUiState';

export interface Args {
    clientId: string;
    userPoolId: string;
}

/**
 * This resolver is intended to be used by the `<PrivateRoute />` component and any other components
 * that need to update the cache according to the current status of the session. For a non-resolver
 * function that does not update the cache, see getCognitoToken.
 */
export const validateCognitoUserSession: Resolver = async (
    obj: Record<string, unknown>,
    args: Args,
    { cache },
): Promise<CognitoSessionResult | WrappedCognitoError> => {
    const { clientId, userPoolId } = args;

    const userPool = new CognitoUserPool({
        UserPoolId: userPoolId,
        ClientId: clientId,
    });

    try {
        const user = await getUser(userPool);

        if (user instanceof Error) {
            throw user;
        } else {
            const session = user === null ? null : await getSession(user);
            const sessionIsValid = _get(session, 'sessionIsValid', false);
            const token = _get(session, 'token', null);
            const expiration = _get(session, 'expiration', null);
            const issuedAt = _get(session, 'issuedAt', null);

            // User is not authenticated; overwrite cached states and return empty values
            if (!sessionIsValid || user === null) {
                cache.writeQuery({
                    query: GET_SESSION,
                    data: {
                        session: {
                            token,
                            codeDeliveryDetails: null,
                            currentUser: null,
                            expiration,
                            isSigningUp: false,
                            issuedAt,
                            sessionIsValid,
                            __typename: 'Session',
                        },
                    },
                });

                cache.writeQuery({
                    query: GET_STATEMENTS_UI_STATE,
                    data: {
                        uiState: {
                            statements: {
                                statementId: null,
                                __typename: 'Statements',
                            },
                            __typename: 'UIState',
                        },
                    },
                });

                return {
                    token: null,
                    expiration: null,
                    issuedAt: null,
                    sessionIsValid: false,
                };
            }

            // User is authenticated, proceed

            const getUserAttributesResult = await getUserAttributes(user);

            if (getUserAttributesResult instanceof Error) {
                throw getUserAttributesResult;
            }

            // .getUsername() actually returns the userSub from what I can tell by testing manually.
            const userSub = user.getUsername();

            const { email, name, username, statementId, trqkUserId } =
                getUserAttributesResult;

            cache.writeQuery({
                query: GET_SESSION,
                data: {
                    session: {
                        token,
                        codeDeliveryDetails: null,
                        currentUser: {
                            id: trqkUserId,
                            userSub,
                            email,
                            name,
                            statementId,
                            username,
                            __typename: 'User',
                        },
                        expiration,
                        isSigningUp: false,
                        issuedAt,
                        sessionIsValid,
                        __typename: 'Session',
                    },
                },
            });

            cache.writeQuery({
                query: GET_STATEMENTS_UI_STATE,
                data: {
                    uiState: {
                        statements: {
                            statementId,
                            __typename: 'Statements',
                        },
                        __typename: 'UIState',
                    },
                },
            });

            return {
                token,
                expiration,
                issuedAt,
                sessionIsValid: true,
            };
        }
    } catch (err) {
        return { error: err };
    }
};

export const VALIDATE_COGNITO_USER_SESSION = gql`
    mutation ValidateCognitoUserSession(
        $clientId: String
        $userPoolId: String
    ) {
        validateCognitoUserSession(
            clientId: $clientId
            userPoolId: $userPoolId
        ) @client
    }
`;
